import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAuthStatus, selectIsAdmin } from './authSlice';
import { Spinner } from '@mmef/shared/components';
import { Callout } from '@mmef/shared/components';

/**
 * Route guard. This is a convenience, not a security boundary — the backend rejects every
 * unauthorised call regardless of what the router lets someone render.
 *
 * The `unknown` branch matters: on a hard refresh the session check is still in flight, and
 * redirecting then would kick signed-in users out of any deep link they opened.
 */
export function RequireAuth({ adminOnly = false }: { adminOnly?: boolean }) {
  const status = useSelector(selectAuthStatus);
  const isAdmin = useSelector(selectIsAdmin);
  const location = useLocation();

  if (status === 'unknown') {
    return <Spinner label="Checking your session" />;
  }

  if (status === 'anonymous') {
    // Remember where they were headed so sign-in can send them back.
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return (
      <Callout tone="error" title="Not available">
        This area is for operators only.
      </Callout>
    );
  }

  return <Outlet />;
}
